import React from "react";
import BaseInput from "../base-input";
import { FormContext } from "../form";
import { toLiteral } from "../../../../helper/locale-utils";
import Dropdown, { DropdownButton, DropdownMenu } from "@adeccoux/tag-ds/dropdown";
import Loader from "@adeccoux/tag-ds/loader";
import Selections from "../select-filter/_select-filter-selections";
import SelectFilterOption from "../select-filter/select-filter-option";
import "./search-select.scss";

export interface ISearchSelectState {
  filterValue: string;
  value: any;
  loading: boolean;
  isSearching: boolean;
  disabled: boolean;
  errors: any[];
  scrollTop: number;
  itemHeight: number;
  windowHeight: number;
  optionField: { label: string; value: string };
  [key: string]: any;
}

class SearchSelect extends BaseInput {
  type = "SearchSelect";
  state: ISearchSelectState;
  scrollContainerRef = React.createRef<HTMLDivElement>();
  hiddenInputRef = React.createRef<HTMLSelectElement>();

  constructor(props: any) {
    super(props);
    this.state = {
      filterValue: "",
      value: props.value ?? props.defaultValue ?? (props.multiple ? [] : ""),
      loading: props.loading || false,
      isSearching: false,
      disabled: props.disabled || false,
      errors: props.errors || [],
      scrollTop: 0,
      itemHeight: 40,
      windowHeight: 250,
      optionField: props?.optionField || { label: "label", value: "value" }
    };
    this.onSelectItem = this.onSelectItem.bind(this);
    this.onRemoveItem = this.onRemoveItem.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.onSelectAll = this.onSelectAll.bind(this);
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.value !== prevProps.value) this.setState({ value: this.props.value });
    if (this.props.loading !== prevProps.loading) this.setState({ loading: this.props.loading });
    if (this.props.disabled !== prevProps.disabled) this.setState({ disabled: this.props.disabled });
  }

  getFilteredItems = () => {
    const options = this.props.options || [];
    const term = this.state.filterValue.trim().toUpperCase();
    if (!term) return options;
    const { label, value } = this.state.optionField;
    return options.filter((opt: any) => 
      opt[label]?.toString().toUpperCase().includes(term) || 
      opt[value]?.toString().toUpperCase().includes(term)
    );
  };

  getOptions = () => this.props.options || [];

  handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    window.requestAnimationFrame(() => {
      this.setState({ scrollTop });
    });
  };

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (this.scrollContainerRef.current) this.scrollContainerRef.current.scrollTop = 0;
    this.setState({ filterValue: value, scrollTop: 0, isSearching: true });
    setTimeout(() => this.setState({ isSearching: false }), 200);
  };

  onSelectItem = (val: any) => {
    const { multiple } = this.props;
    let newValue;
    
    if (!multiple) {
      newValue = val;
    } else {
      const currentValues = Array.isArray(this.state.value) ? this.state.value : [];
      newValue = currentValues.includes(val) 
        ? currentValues.filter((v: any) => v !== val) 
        : [...currentValues, val];
    }
    this.updateValue(newValue);
  };

  onRemoveItem = (val: any) => {
    const { multiple } = this.props;
    let newValue;
    if (multiple) {
      const currentValues = Array.isArray(this.state.value) ? this.state.value : [];
      newValue = currentValues.filter((v: any) => v !== val);
    } else {
      newValue = "";
    }
    this.updateValue(newValue);
  };

  onSelectAll = (allValues: any[], isAllSelected: boolean) => {
    this.setState({ isSearching: true });
    setTimeout(() => {
      const newValue = isAllSelected ? [] : [...allValues];
      this.updateValue(newValue);
      this.setState({ isSearching: false });
    }, 10);
  };

  updateValue = (newValue: any) => {
    const fakeEvent = {
      target: {
        id: this.props.id,
        name: this.props.name,
        value: newValue,
        inputValue: newValue,
      },
    };
    this.processChange(newValue, fakeEvent);
  };

  renderOptions(filtered: any[], startIndex: number, endIndex: number, showSelectAll: boolean, allOptionValues: any[], isAllSelected: boolean) {
    const { value, itemHeight, optionField } = this.state;
    const { label: labelKey, value: valueKey } = optionField;
    const items = [];

    for (let i = startIndex; i < endIndex; i++) {
      if (showSelectAll && i === 0) {
        items.push(
          <div key="select-all" className="virtual-option-wrapper" style={{ top: 0, height: itemHeight, position: 'absolute', width: '100%' }}>
            <SelectFilterOption
              multiple={true}
              selected={isAllSelected}
              onSelectItem={() => this.onSelectAll(allOptionValues, isAllSelected)}
              onRemoveItem={() => this.onSelectAll(allOptionValues, isAllSelected)}
              value="all"
              label={toLiteral({ id: "select.all.button" })}
            />
          </div>
        );
        continue;
      }

      const dataIndex = showSelectAll ? i - 1 : i;
      const option = filtered[dataIndex];
      if (!option) continue;

      // Safe check for both single (string) and multiple (array)
      const isSelected = this.props.multiple 
        ? (Array.isArray(value) && value.includes(option[valueKey]))
        : (value === option[valueKey]);
      
      items.push(
        <div
          key={option[valueKey]}
          className="virtual-option-wrapper"
          style={{ top: i * itemHeight, height: itemHeight, position: 'absolute', width: '100%' }}
        >
          <SelectFilterOption 
            value={option[valueKey]} 
            label={toLiteral({ id: option[labelKey] })} 
            onSelectItem={this.onSelectItem}
            onRemoveItem={this.onRemoveItem}
            selected={isSelected} 
            multiple={this.props.multiple}
          />
        </div>
      );
    }
    return items;
  }

  renderHiddenInput() {
    let selectedValues = [];
    if (Array.isArray(this.state.value)) {
      selectedValues = this.state.value;
    } else if (this.state.value !== "" && this.state.value !== undefined && this.state.value !== null) {
      selectedValues = [this.state.value];
    }

    return (
      <div className="hidden">
        <select name={this.props.name} multiple={this.props.multiple} ref={this.hiddenInputRef} value={this.state.value}>
          {!this.props.multiple && selectedValues.length === 0 && <option value="" />}
          {selectedValues.map((selectedValue: any, index: number) => (
            <option key={`${selectedValue}-${index}`} value={selectedValue}>
              {selectedValue}
            </option>
          ))}
        </select>
      </div>
    );
  }

  render() {
    this.processCSSClasses();
    const { scrollTop, itemHeight, windowHeight, value, optionField } = this.state;
    const options = this.getOptions();
    const filtered = this.getFilteredItems();
    
    const allOptionValues = options.map((opt: any) => opt[optionField.value]);
    const showSelectAll = this.props.showSelectAllOption && this.props.multiple && allOptionValues.length > 0;
    
    // Safety check for isAllSelected
    const isAllSelected = showSelectAll && 
                          Array.isArray(value) && 
                          value.length === allOptionValues.length && 
                          allOptionValues.length > 0;

    const totalRows = showSelectAll ? filtered.length + 1 : filtered.length;
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - 4);
    const endIndex = Math.min(totalRows, Math.floor((scrollTop + windowHeight) / itemHeight) + 4);

    const hasValue = this.props.multiple 
        ? (Array.isArray(value) && value.length > 0) 
        : (value !== "" && value !== undefined && value !== null);

    return (
      <div className={`input-wrapper ${this.state.disabled ? "_disabled" : ""} ${this.validationClass} ${this.props.className || ""}`}>
        {this.props.label && (
          <label className={this.props.labalClassName || "caption mb1"}>
            {this.validationClass?.includes("validation-required") && <small className="required">*</small>}
            {this.props.label}
          </label>
        )}

        {this.renderHiddenInput()}

        <Dropdown id={`search-select-${this.props.name}`}>
          <DropdownButton disabled={this.state.disabled}>
            <div className="input-container">
              <input
                type="text"
                className="select-text-input"
                placeholder={this.props.placeholder || toLiteral({ id: "Search..." })}
                value={this.state.filterValue}
                onChange={this.handleSearchChange}
                disabled={this.state.disabled}
                data-testid={this.props?.["data-testid"] || this.props.id || "search-select"}
              />
              {this.state.loading || this.state.isSearching ? <Loader spinner className="spinner-box" /> : <span className="material-icons input-icon-box">search</span>}
            </div>
          </DropdownButton>

          <DropdownMenu>
            <div 
              ref={this.scrollContainerRef} 
              onScroll={this.handleScroll} 
              className="search-select-menu-container custom-scrollbar"
              style={{ height: windowHeight, overflowY: 'auto', position: 'relative' }}
            >
              <div className="virtual-scroll-spacer" style={{ height: `${totalRows * itemHeight}px`, position: 'relative' }}>
                {this.renderOptions(filtered, startIndex, endIndex, showSelectAll, allOptionValues, isAllSelected)}
              </div>
              {filtered.length === 0 && !this.state.isSearching && <div className="no-results-message">{toLiteral({ id: "No results found" })}</div>}
            </div>
          </DropdownMenu>
        </Dropdown>

        {!this.props.hideSelections && hasValue && (
          <Selections
            name={this.props.name}
            onRemoveItem={this.onRemoveItem}
            items={options.map((opt: any) => ({
              props: { value: opt?.[optionField.value], label: opt?.[optionField.label] },
            }))}
            value={this.state.value}
            disabled={this.state.disabled}
          />
        )}
        {this.renderErrorMessage()}
      </div>
    );
  }
}

SearchSelect.contextType = FormContext;
export default SearchSelect;
